import { useRef, useState } from 'react'
import useThemeAI from './useThemeAI'
import type { ThemeAITaskSampleResponse } from './useThemeAITasks'
import type {
  AutoCoderCode,
  CarbonInterviewRawDataRow,
  CreateThemeAITaskRequest,
  SourceVariable,
  ThemeAICodeFrame,
  ThemeAICodeFrameVersion,
  ThemeAIGroup,
  ThemeAITask,
  ThemeAITheme,
} from '../types/gotypes'

/** Theme list → the agent's editable proposal. */
export const toProposedThemes = (themes: ThemeAITheme[]): AutoCoderCode[] =>
  themes.map((theme) => ({ code: String(theme.code), text: theme.name, description: theme.assignment_explanation }))

// Code 99 is reserved for "Junk / no answer" (#1891, themeai.JunkThemeCode):
// new themes step over it, so a frame of 100 themes is 1–98, 100, 101.
const JUNK_THEME_CODE = 99
const nextThemeCode = (after: number): number => (after + 1 === JUNK_THEME_CODE ? after + 2 : after + 1)

/**
 * A proposal → frame themes. A theme whose code already exists keeps its
 * stable id (spec #916 §4.4: ids survive renames); new themes get the next
 * free code, never the reserved junk code.
 */
export const toFrameThemes = (proposed: AutoCoderCode[], existing: ThemeAITheme[]): ThemeAITheme[] => {
  const byCode = new Map(existing.map((theme) => [String(theme.code), theme]))
  let lastCode = Math.max(0, ...existing.map((theme) => theme.code))
  return proposed.map((code, index) => {
    const kept = byCode.get(code.code)
    const name = code.text || code.code
    if (kept) return { ...kept, name, assignment_explanation: code.description, order: index }
    lastCode = nextThemeCode(lastCode)
    return { id: crypto.randomUUID(), code: lastCode, name, assignment_explanation: code.description, order: index }
  })
}

/** How many responses the agent reads when proposing themes (Matt, 21 Sep: 50, 100 or 200). */
export const AGENT_SAMPLE_SIZES = [50, 100, 200] as const

interface UseThemeAITaskAgentOptions {
  customer: string
  job: string
  task?: ThemeAITask
  frameThemes: ThemeAITheme[]
  frameGroups?: ThemeAIGroup[]
  /** True when the task's frame is a standard one (#1492): it cannot take a new version. */
  frameIsStandard?: boolean
  loadTaskSample: (size: number) => Promise<ThemeAITaskSampleResponse>
  updateTask: (id: string, payload: Partial<CreateThemeAITaskRequest>) => Promise<ThemeAITask>
  saveThemes: (
    themes: ThemeAITheme[],
    options: { frameId?: string; name?: string; groups?: ThemeAIGroup[] },
  ) => Promise<ThemeAICodeFrame | ThemeAICodeFrameVersion>
}

// The ThemeAI task's agent: the side pane's open state, the sample and study
// context it reads, its conversation, and saving what it (or the user, by
// hand) proposes as the task's code frame.
const useThemeAITaskAgent = ({
  customer,
  job,
  task,
  frameThemes,
  frameGroups,
  frameIsStandard = false,
  loadTaskSample,
  updateTask,
  saveThemes,
}: UseThemeAITaskAgentOptions) => {
  const themeAI = useThemeAI({ customer, job })
  const [open, setOpen] = useState(false)
  const [sampleSize, setSampleSize] = useState<number>(AGENT_SAMPLE_SIZES[2])
  const prepared = useRef<{ key: string; rows: CarbonInterviewRawDataRow[]; variable: SourceVariable }>()

  const handleOpen = (mode: 'build' | 'refine') => {
    if (mode === 'refine') themeAI.setThemes(toProposedThemes(frameThemes))
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  // The agent sees the organisation and job context, the question, the saved
  // task context and a sample of the responses; a change to any of them
  // re-prepares it.
  const handleSend = async (model: string, input: string) => {
    if (!task) return
    const studyContext = task.study_context?.trim()
    const key = `${task.data_revision}:${studyContext || ''}:${sampleSize}`
    let context = prepared.current
    if (!context || context.key !== key) {
      const sample = await loadTaskSample(sampleSize)
      const question = task.data_source?.job_variable?.question || 'Uploaded response'
      const jobContext = sample.job_context?.trim()
      const variable = {
        name: sample.variable_name || 'source_text',
        folder_path: task.data_source?.job_variable?.folder_path || '',
        question: [
          question,
          studyContext && `About this study: ${studyContext}`,
          jobContext && `About the client and this job: ${jobContext}`,
        ].filter(Boolean).join('\n\n'),
      } as SourceVariable
      context = { key, rows: sample.rows, variable }
      prepared.current = context
      themeAI.setData(context.rows)
      themeAI.setVariable(context.variable)
    }
    await themeAI.chat(model, input, { data: context.rows, variable: context.variable })
  }

  /**
   * Saves a theme list as the task's frame: a new version of its frame, or a
   * new frame. A standard frame is the same for every client and takes no
   * versions, so a change to one becomes the user's own new frame.
   */
  const handleSaveThemes = async (proposed: AutoCoderCode[]) => {
    if (!task || proposed.length === 0) return
    const themes = toFrameThemes(proposed, frameThemes)
    if (task.code_frame_id && !frameIsStandard) {
      const saved = await saveThemes(themes, { frameId: task.code_frame_id, groups: frameGroups })
      if ('version' in saved) {
        await updateTask(task.id, { code_frame_id: task.code_frame_id, code_frame_version: saved.version })
      }
    } else {
      const saved = await saveThemes(themes, { name: `${task.name} themes` })
      if ('latest_version' in saved) {
        await updateTask(task.id, { code_frame_id: saved.id, code_frame_version: saved.latest_version || 1 })
      }
    }
  }

  const handleSaveProposedThemes = async () => {
    await handleSaveThemes(themeAI.themes)
    setOpen(false)
  }

  /** Forget the prepared sample, e.g. after the data is replaced. */
  const handleReset = () => {
    prepared.current = undefined
    themeAI.setData([])
  }

  return {
    open,
    handleOpen,
    handleClose,
    sampleSize,
    setSampleSize,
    messages: themeAI.extractChat.messages.map((message) => ({
      role: message.role,
      content: typeof message.content === 'string' ? message.content : '',
    })),
    proposedThemes: themeAI.themes,
    setProposedThemes: themeAI.setThemes,
    loading: themeAI.extractIsLoading,
    error: themeAI.extractError || undefined,
    handleSend,
    handleSaveThemes,
    handleSaveProposedThemes,
    handleReset,
  }
}

export default useThemeAITaskAgent
